import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  ChevronRight, 
  Users, 
  Lightbulb, 
  Clock, 
  Sparkles,
  Calculator,
  BookOpen,
  Building,
  FileText,
  DollarSign,
  PiggyBank,
  Linkedin,
  Facebook,
  Instagram,
  ArrowRight
} from "lucide-react";
import ServicePage from "./components/ServicePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HTTP status code constants
const HTTP_TOO_MANY_REQUESTS = 429;

// Brand Information
const BRAND = {
  name: "Economy Accounting",
  fullName: "Economy Accounting and Tax Services",
  email: "sales@economyaccounting.ca",
  locations: {
    edmonton: {
      city: "Edmonton",
      address: "9190 34a Ave NW unit # 107, Edmonton, AB T6E 5P4",
      phones: ["647-772-4857", "780-522-2220"],
      email: "sales@economyaccounting.ca"
    },
    yellowknife: {
      city: "Yellowknife",
      address: "850 Dusseault Court, Yellowknife, NT X1A 2Z4",
      phones: ["867-444-6556"],
      email: "info@economyaccounting.ca"
    }
  }
};

// ScrollToTop component for route changes
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
};

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Home", href: "#home" },
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Process", href: "#process" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "Contact", href: "#contact" }
  ];

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ 
        backgroundColor: isScrolled ? '#FFFFFF' : 'transparent',
        boxShadow: isScrolled ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'
      }}
      data-testid="nav-header"
    >
      {/* Top Utility Bar - hidden when scrolled */}
      {!isScrolled && (
        <div className="bg-[#1A2744] text-white py-2 px-6 text-sm hidden md:block" data-testid="utility-bar">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-6">
              <span className="text-[#C8973A] font-medium">Edmonton:</span>
              <a href="tel:647-772-4857" className="flex items-center gap-1 hover:text-[#C8973A] transition-colors">
                <Phone size={12} />
                <span>647-772-4857</span>
              </a>
              <span className="text-white/30">|</span>
              <span className="text-[#C8973A] font-medium">Yellowknife:</span>
              <a href="tel:867-444-6556" className="flex items-center gap-1 hover:text-[#C8973A] transition-colors">
                <Phone size={12} />
                <span>867-444-6556</span>
              </a>
            </div>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 hover:text-[#C8973A] transition-colors">
              <Mail size={14} />
              <span>{BRAND.email}</span>
            </a>
          </div>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="w-full">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo">
            <span className="font-serif text-2xl font-bold text-[#C8973A]">
              Economy
            </span>
            <span className={`font-serif text-2xl font-bold ${isScrolled ? "text-[#1A2744]" : "text-white"}`}>
              {" "}Accounting
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`nav-link font-medium text-sm uppercase tracking-wide transition-colors ${
                  isScrolled ? "text-[#1A2744] hover:text-[#C8973A]" : "text-white hover:text-[#C8973A]"
                } ${activeSection === link.href.slice(1) ? "text-[#C8973A]" : ""}`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              className="btn-primary px-6 py-3 font-medium text-sm uppercase tracking-wide"
              data-testid="nav-cta-btn"
            >
              Contact Us
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X size={28} className={isScrolled ? "text-[#1A2744]" : "text-white"} />
            ) : (
              <Menu size={28} className={isScrolled ? "text-[#1A2744]" : "text-white"} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg" data-testid="mobile-menu">
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-[#1A2744] font-medium py-2 border-b border-gray-100"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                className="btn-primary px-6 py-3 text-center font-medium text-sm uppercase tracking-wide"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact Us
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/425122/pexels-photo-425122.jpeg')" }}
      />
      
      {/* Gradient Overlay */}
      <div className="hero-gradient absolute inset-0" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-32">
        <div className="max-w-2xl">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C8973A] font-semibold mb-4 block">
            {BRAND.fullName}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
            Precision, Insight, and Trust for Your Financial Future.
          </h1>
          <p className="text-white/80 text-lg md:text-xl mb-8 leading-relaxed max-w-xl">
            A trusted accounting and tax advisory firm with offices in Edmonton and Yellowknife, delivering Big 4 expertise with personalized care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="#services" 
              className="btn-primary px-8 py-4 font-medium text-sm uppercase tracking-wide inline-flex items-center justify-center gap-2"
              data-testid="hero-services-btn"
            >
              Our Services
              <ChevronRight size={18} />
            </a>
            <a 
              href="#contact" 
              className="btn-ghost px-8 py-4 font-medium text-sm uppercase tracking-wide inline-flex items-center justify-center"
              data-testid="hero-contact-btn"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Stats Strip - Removed */}
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section 
      id="about" 
      className="bg-[#F5F6F8] py-16 md:py-24 px-6 md:px-12 lg:px-24"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <div>
            <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C8973A] font-semibold mb-4 block">
              About Economy Accounting
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2744] leading-tight mb-6">
              Simplifying Finances,<br />Amplifying Success.
            </h2>
            <p className="text-[#3D3D3D] text-base md:text-lg leading-relaxed mb-6">
              We are proud to be a top-notch accounting firm with a highly skilled team of CPAs and financial experts who provide financial solutions to start-up entrepreneurs, small business owners, and large corporations alike.
            </p>
            <p className="text-[#3D3D3D] text-base md:text-lg leading-relaxed mb-6">
              Our team has years of experience dealing with complex financial challenges and making the right decisions to help our clients achieve their business objectives. By choosing us, you can focus on your core business without worrying about complicated financial calculations and decision-making.
            </p>
            <p className="text-[#3D3D3D] text-base md:text-lg leading-relaxed mb-8">
              We offer virtual appointments with our expert CPAs — book an appointment today and let us take care of the numbers while you focus on growing your business.
            </p>
            <a 
              href="#contact" 
              className="btn-secondary px-8 py-4 font-medium text-sm uppercase tracking-wide inline-flex items-center gap-2"
              data-testid="about-cta-btn"
            >
              Book a Consultation
              <ArrowRight size={18} />
            </a>
          </div>

          {/* Image */}
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1714974528683-8940bd8311aa" 
              alt="Accounting professionals meeting and reviewing documents"
              className="w-full h-auto object-cover shadow-lg"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyUsSection = () => {
  const features = [
    {
      icon: <Users className="w-8 h-8 text-[#C8973A]" />,
      title: "Our Priority: Clients",
      description: "Our main priority will always be our customers. We strive to deliver excellent service, respect your priorities, and lead you toward the factors that drive your success."
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-[#C8973A]" />,
      title: "Innovation",
      description: "We employ the most significant tools available. Automation and the right technologies save costs and time — we test and implement them in your business."
    },
    {
      icon: <Clock className="w-8 h-8 text-[#C8973A]" />,
      title: "Response Time",
      description: "We understand your time is valuable. We offer a guaranteed 24-hour email response time and always keep you in the loop on any work being done for you."
    },
    {
      icon: <Sparkles className="w-8 h-8 text-[#C8973A]" />,
      title: "Simplicity",
      description: "We offer an effective solution to every complicated business issue. As a business professional, you deserve clear, straightforward answers — not jargon."
    }
  ];

  return (
    <section 
      id="why-us" 
      className="bg-white py-16 md:py-24 px-6 md:px-12 lg:px-24"
      data-testid="why-us-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C8973A] font-semibold mb-4 block">
            Why Choose Us
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2744] leading-tight">
            Your Partners in Building<br />a Strong Financial Future.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div 
              key={feature.title} 
              className="feature-card bg-white border border-[#E5E7EB] p-8"
              data-testid={`feature-card-${index}`}
            >
              <div className="mb-6">{feature.icon}</div>
              <h3 className="font-serif text-xl text-[#1A2744] font-semibold mb-4">{feature.title}</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Calculator className="w-10 h-10 text-[#C8973A]" />,
      title: "Accounting",
      slug: "accounting",
      description: "Comprehensive financial reporting and analysis to ensure compliance and support strategic business decisions."
    },
    {
      icon: <BookOpen className="w-10 h-10 text-[#C8973A]" />,
      title: "Bookkeeping",
      slug: "bookkeeping",
      description: "Accurate recording and management of daily financial transactions for streamlined operations and clarity."
    },
    {
      icon: <Building className="w-10 h-10 text-[#C8973A]" />,
      title: "Business Setup",
      slug: "business-setup",
      description: "Expert guidance to establish your business with solid financial and legal foundations."
    },
    {
      icon: <FileText className="w-10 h-10 text-[#C8973A]" />,
      title: "Tax Planning",
      slug: "tax-planning",
      description: "Strategic tax solutions to minimize liabilities and maximize savings while ensuring compliance."
    },
    {
      icon: <DollarSign className="w-10 h-10 text-[#C8973A]" />,
      title: "Payroll",
      slug: "payroll",
      description: "Efficient payroll processing with precise calculations and timely disbursements."
    },
    {
      icon: <PiggyBank className="w-10 h-10 text-[#C8973A]" />,
      title: "Wealth Management",
      slug: "wealth-management",
      description: "Tailored financial strategies to grow, preserve, and manage your wealth effectively."
    }
  ];

  return (
    <section 
      id="services" 
      className="bg-[#F5F6F8] py-16 md:py-24 px-6 md:px-12 lg:px-24"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C8973A] font-semibold mb-4 block">
            What We Do
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2744] leading-tight mb-4">
            Services Designed for<br />Your Business Growth.
          </h2>
          <p className="text-[#6B7280] text-base md:text-lg max-w-2xl mx-auto">
            Here are our best services and methods tailored for your business needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={service.slug} 
              className="service-card bg-white border border-[#E5E7EB] p-8 md:p-10"
              data-testid={`service-card-${index}`}
            >
              <div className="mb-6">{service.icon}</div>
              <h3 className="font-serif text-2xl text-[#1A2744] font-semibold mb-4">{service.title}</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed mb-6">{service.description}</p>
              <Link 
                to={`/services/${service.slug}`}
                className="inline-flex items-center gap-2 text-[#C8973A] font-medium text-sm hover:gap-3 transition-all"
                data-testid={`service-link-${service.slug}`}
              >
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a 
            href="#contact" 
            className="btn-secondary px-8 py-4 font-medium text-sm uppercase tracking-wide inline-flex items-center gap-2"
            data-testid="services-cta-btn"
          >
            Contact Us
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Free Assessment",
      description: "Initial consultation and evaluation of your business needs. No cost, no obligation."
    },
    {
      number: "02",
      title: "Receive a Quote",
      description: "Transparent, itemized pricing for the services you need — no surprises."
    },
    {
      number: "03",
      title: "We Get to Work",
      description: "Expert execution of tasks by our team of CPAs and financial professionals."
    },
    {
      number: "04",
      title: "Reporting",
      description: "Comprehensive insights and updates delivered to keep you fully informed."
    }
  ];

  return (
    <section 
      id="process" 
      className="bg-white py-16 md:py-24 px-6 md:px-12 lg:px-24"
      data-testid="process-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C8973A] font-semibold mb-4 block">
            Our Process
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2744] leading-tight">
            Our Working Process
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connecting Line (desktop only) */}
          <div className="hidden lg:block process-line" style={{ top: "60px" }} />
          
          {steps.map((step, index) => (
            <div 
              key={step.number} 
              className="process-step relative bg-white p-8 z-10"
              data-testid={`process-step-${index}`}
            >
              <div className="process-number font-serif">{step.number}</div>
              <div className="relative z-10">
                <div className="w-12 h-12 bg-[#C8973A] text-white flex items-center justify-center font-bold text-lg mb-6">
                  {step.number}
                </div>
                <h3 className="font-serif text-xl text-[#1A2744] font-semibold mb-3">{step.title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "Economy Accounting transformed our finances! Their tailored solutions and proactive advice helped us streamline operations and focus on growth.",
      name: "Jane D.",
      role: "Business Owner",
      avatar: "https://ui-avatars.com/api/?name=Jane+D&background=1A2744&color=C8973A&size=64"
    },
    {
      quote: "The team at Economy Accounting is incredibly reliable and professional. They make managing our payroll and taxes completely stress-free!",
      name: "Michael R.",
      role: "CEO, Tech Startup",
      avatar: "https://ui-avatars.com/api/?name=Michael+R&background=1A2744&color=C8973A&size=64"
    },
    {
      quote: "Economy Accounting's wealth management services gave me clarity and confidence in planning for the future. Highly recommend their expertise.",
      name: "Sarah L.",
      role: "Entrepreneur",
      avatar: "https://ui-avatars.com/api/?name=Sarah+L&background=1A2744&color=C8973A&size=64"
    }
  ];

  return (
    <section 
      id="testimonials" 
      className="bg-[#1A2744] py-16 md:py-24 px-6 md:px-12 lg:px-24"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C8973A] font-semibold mb-4 block">
            Client Testimonials
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            What Our Clients<br />Are Saying.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.name} 
              className="testimonial-card p-8"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="quote-mark mb-4">"</div>
              <p className="text-white/90 text-base leading-relaxed mb-8 italic">
                {testimonial.quote}
              </p>
              <div className="flex items-center gap-4">
                <img 
                  src={testimonial.avatar} 
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full"
                  loading="lazy"
                />
                <div>
                  <div className="text-white font-semibold">{testimonial.name}</div>
                  <div className="text-white/60 text-sm">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Banner Section
const CTABannerSection = () => {
  return (
    <section 
      className="bg-[#C8973A] py-16 md:py-20 px-6 md:px-12 lg:px-24"
      data-testid="cta-banner-section"
    >
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2744] leading-tight mb-6">
          Have We Convinced You Yet?
        </h2>
        <p className="text-[#1A2744]/80 text-lg md:text-xl mb-8 max-w-2xl mx-auto">
          You see that we're different — let's chat! Come in for a coffee, we can't wait to get to know you.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <a 
            href={`tel:${BRAND.locations.edmonton.phones[0]}`}
            className="bg-[#1A2744] text-white px-8 py-4 font-medium text-sm uppercase tracking-wide inline-flex items-center justify-center gap-2 hover:bg-[#0f1829] transition-colors"
            data-testid="cta-call-btn"
          >
            <Phone size={18} />
            Call Us: {BRAND.locations.edmonton.phones[0]}
          </a>
          <a 
            href="#contact"
            className="bg-white text-[#1A2744] border-2 border-white px-8 py-4 font-medium text-sm uppercase tracking-wide inline-flex items-center justify-center gap-2 hover:bg-transparent hover:text-white transition-colors"
            data-testid="cta-message-btn"
          >
            <Mail size={18} />
            Send Us a Message
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    website: "" // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const services = [
    "Accounting",
    "Bookkeeping",
    "Business Setup",
    "Tax Planning",
    "Payroll",
    "Wealth Management",
    "Other"
  ];

  // Disposable email domains to check on frontend
  const disposableEmailDomains = [
    'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'throwaway.email',
    'fakeinbox.com', 'yopmail.com', '10minutemail.com', 'temp-mail.org',
    'maildrop.cc', 'getnada.com', 'mohmal.com', 'tempail.com'
  ];

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;
    const domain = email.split('@')[1]?.toLowerCase();
    return !disposableEmailDomains.includes(domain);
  };

  const isValidPhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrorMessage(""); // Clear error on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    // Frontend validation
    if (!isValidEmail(formData.email)) {
      setErrorMessage("Please enter a valid business or personal email address.");
      setIsSubmitting(false);
      return;
    }

    if (!isValidPhone(formData.phone)) {
      setErrorMessage("Please enter a valid phone number (10+ digits).");
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success");
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
        website: ""
      });
    } catch (error) {
      if (error.response?.status === HTTP_TOO_MANY_REQUESTS) {
        setErrorMessage("Too many submissions. Please try again in an hour.");
      } else if (error.response?.data?.detail) {
        // Handle validation errors from backend
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          setErrorMessage(detail.map(d => d.msg).join(". "));
        } else {
          setErrorMessage(detail);
        }
      } else {
        setErrorMessage("There was an error submitting your message. Please try again.");
      }
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="bg-[#F5F6F8] py-16 md:py-24 px-6 md:px-12 lg:px-24"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C8973A] font-semibold mb-4 block">
            Contact Us
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A2744] leading-tight">
            Send a Message
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info - Two Locations */}
          <div className="space-y-6">
            {/* Edmonton Location */}
            <div className="bg-white p-6 md:p-8 border border-[#E5E7EB]">
              <h3 className="font-serif text-xl text-[#1A2744] font-semibold mb-6 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#C8973A]" />
                Edmonton Office
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-[#1A2744] mb-1">Address</div>
                  <div className="text-[#6B7280] text-sm">{BRAND.locations.edmonton.address}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#1A2744] mb-1">Phone</div>
                  {BRAND.locations.edmonton.phones.map((phone) => (
                    <a key={phone} href={`tel:${phone}`} className="text-[#6B7280] text-sm hover:text-[#C8973A] transition-colors block">
                      {phone}
                    </a>
                  ))}
                </div>
                <div>
                  <div className="text-sm font-medium text-[#1A2744] mb-1">Email</div>
                  <a href={`mailto:${BRAND.locations.edmonton.email}`} className="text-[#6B7280] text-sm hover:text-[#C8973A] transition-colors">
                    {BRAND.locations.edmonton.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Yellowknife Location */}
            <div className="bg-white p-6 md:p-8 border border-[#E5E7EB]">
              <h3 className="font-serif text-xl text-[#1A2744] font-semibold mb-6 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#C8973A]" />
                Yellowknife Office
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-[#1A2744] mb-1">Address</div>
                  <div className="text-[#6B7280] text-sm">{BRAND.locations.yellowknife.address}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-[#1A2744] mb-1">Phone</div>
                  {BRAND.locations.yellowknife.phones.map((phone) => (
                    <a key={phone} href={`tel:${phone}`} className="text-[#6B7280] text-sm hover:text-[#C8973A] transition-colors block">
                      {phone}
                    </a>
                  ))}
                </div>
                <div>
                  <div className="text-sm font-medium text-[#1A2744] mb-1">Email</div>
                  <a href={`mailto:${BRAND.locations.yellowknife.email}`} className="text-[#6B7280] text-sm hover:text-[#C8973A] transition-colors">
                    {BRAND.locations.yellowknife.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Office Hours */}
            <div className="bg-white p-6 md:p-8 border border-[#E5E7EB]">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#C8973A]" />
                <div>
                  <div className="text-sm font-medium text-[#1A2744]">Office Hours</div>
                  <div className="text-[#6B7280] text-sm">Mon – Fri, 9:00 AM – 5:00 PM</div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              <a href="#" className="social-icon text-[#1A2744] hover:text-[#C8973A]" aria-label="LinkedIn" data-testid="social-linkedin">
                <Linkedin size={24} />
              </a>
              <a href="#" className="social-icon text-[#1A2744] hover:text-[#C8973A]" aria-label="Facebook" data-testid="social-facebook">
                <Facebook size={24} />
              </a>
              <a href="#" className="social-icon text-[#1A2744] hover:text-[#C8973A]" aria-label="Instagram" data-testid="social-instagram">
                <Instagram size={24} />
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-[#1A2744] mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    className="form-input w-full h-12 border border-[#E5E7EB] bg-white px-4 text-[#3D3D3D] placeholder:text-[#6B7280]"
                    placeholder="John"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-[#1A2744] mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className="form-input w-full h-12 border border-[#E5E7EB] bg-white px-4 text-[#3D3D3D] placeholder:text-[#6B7280]"
                    placeholder="Doe"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#1A2744] mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input w-full h-12 border border-[#E5E7EB] bg-white px-4 text-[#3D3D3D] placeholder:text-[#6B7280]"
                  placeholder="john@example.com"
                  data-testid="input-email"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#1A2744] mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input w-full h-12 border border-[#E5E7EB] bg-white px-4 text-[#3D3D3D] placeholder:text-[#6B7280]"
                  placeholder="+1 (555) 000-0000"
                  data-testid="input-phone"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-[#1A2744] mb-2">
                  Service Interested In *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="form-input w-full h-12 border border-[#E5E7EB] bg-white px-4 text-[#3D3D3D]"
                  data-testid="select-service"
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#1A2744] mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="form-input w-full border border-[#E5E7EB] bg-white px-4 py-3 text-[#3D3D3D] placeholder:text-[#6B7280] resize-none"
                  placeholder="Tell us about your needs..."
                  data-testid="textarea-message"
                />
              </div>

              {/* Honeypot field - hidden from users, visible to bots */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {submitStatus === "success" && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3" data-testid="form-success">
                  Thank you for your message! We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === "error" && errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3" data-testid="form-error">
                  {errorMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full py-4 font-medium text-sm uppercase tracking-wide disabled:opacity-50"
                data-testid="submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      await axios.post(`${API}/newsletter`, { email });
      setSubscribeStatus("success");
      setEmail("");
    } catch (error) {
      setSubscribeStatus("error");
    }
  };

  const serviceLinks = [
    { label: "Accounting", href: "/services/accounting" },
    { label: "Bookkeeping", href: "/services/bookkeeping" },
    { label: "Business Setup", href: "/services/business-setup" },
    { label: "Tax Planning", href: "/services/tax-planning" },
    { label: "Payroll", href: "/services/payroll" },
    { label: "Wealth Management", href: "/services/wealth-management" }
  ];

  const usefulLinks = [
    { label: "About Us", href: "#about" },
    { label: "Our Process", href: "#process" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "Contact Us", href: "#contact" },
    { label: "Privacy Policy", href: "#" }
  ];

  return (
    <footer className="bg-[#1A2744] text-white py-16 px-6 md:px-12 lg:px-24" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <span className="font-serif text-2xl font-bold text-[#C8973A]">Economy</span>
              <span className="font-serif text-2xl font-bold text-white"> Accounting</span>
            </Link>
            <p className="text-white/60 text-sm mb-6">
              Building Financial Foundations, Together.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full h-12 bg-white/10 border border-white/20 px-4 text-white placeholder:text-white/50 focus:outline-none focus:border-[#C8973A]"
                data-testid="newsletter-input"
              />
              <button 
                type="submit"
                className="w-full h-12 bg-[#C8973A] text-white font-medium text-sm uppercase tracking-wide hover:bg-[#B3852E] transition-colors"
                data-testid="newsletter-btn"
              >
                Subscribe
              </button>
              {subscribeStatus === "success" && (
                <p className="text-green-400 text-sm">Successfully subscribed!</p>
              )}
            </form>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Our Services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-white/60 hover:text-[#C8973A] transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Useful Links Column */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {usefulLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-white/60 hover:text-[#C8973A] transition-colors text-sm">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations Column */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Our Offices</h4>
            
            {/* Edmonton */}
            <div className="mb-6">
              <div className="text-[#C8973A] font-medium text-sm mb-2">Edmonton</div>
              <ul className="space-y-2 text-white/60 text-sm">
                <li>{BRAND.locations.edmonton.address}</li>
                <li>
                  {BRAND.locations.edmonton.phones.map((phone) => (
                    <a key={phone} href={`tel:${phone}`} className="hover:text-[#C8973A] transition-colors block">
                      {phone}
                    </a>
                  ))}
                </li>
              </ul>
            </div>
            
            {/* Yellowknife */}
            <div>
              <div className="text-[#C8973A] font-medium text-sm mb-2">Yellowknife</div>
              <ul className="space-y-2 text-white/60 text-sm">
                <li>{BRAND.locations.yellowknife.address}</li>
                <li>
                  <a href={`tel:${BRAND.locations.yellowknife.phones[0]}`} className="hover:text-[#C8973A] transition-colors">
                    {BRAND.locations.yellowknife.phones[0]}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#C8973A]/30 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            © 2025 {BRAND.name}. All Rights Reserved.
          </p>
          <div className="flex gap-4">
            <a href="#" className="social-icon text-white/60 hover:text-[#C8973A]" aria-label="LinkedIn">
              <Linkedin size={20} />
            </a>
            <a href="#" className="social-icon text-white/60 hover:text-[#C8973A]" aria-label="Facebook">
              <Facebook size={20} />
            </a>
            <a href="#" className="social-icon text-white/60 hover:text-[#C8973A]" aria-label="Instagram">
              <Instagram size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: "-80px 0px -50% 0px" }
    );

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => observer.observe(section));

    return () => sections.forEach((section) => observer.unobserve(section));
  }, []);

  return (
    <>
      <Navigation activeSection={activeSection} />
      <HeroSection />
      <AboutSection />
      <WhyUsSection />
      <ServicesSection />
      <ProcessSection />
      <TestimonialsSection />
      <CTABannerSection />
      <ContactSection />
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services/:serviceSlug" element={<ServicePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
